import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Blahblah } from '@/types';
import { usePlayerStore } from '@/stores/playerStore';
import { useFeedStore } from '@/stores/feedStore';
import { cn, formatDuration, formatRelativeTime } from '@/lib/utils';
import { ShareToGroupModal } from './ShareToGroupModal';

interface BlahblahCardProps {
  blahblah: Blahblah;
  showActions?: boolean;
}

export function BlahblahCard({ blahblah, showActions = true }: BlahblahCardProps) {
  const navigate = useNavigate();
  const { currentId, isPlaying, progress, play, pause } = usePlayerStore();
  const { likeBlahblah, unlikeBlahblah, favoriteBlahblah, unfavoriteBlahblah } = useFeedStore();
  const [showMenu, setShowMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const isCurrentlyPlaying = currentId === blahblah.id && isPlaying;
  const playbackProgress = currentId === blahblah.id ? progress : 0;
  const progressPercent = (playbackProgress / blahblah.durationMs) * 100;

  const handlePlayPause = () => {
    if (isCurrentlyPlaying) {
      pause();
    } else {
      play(blahblah.id, blahblah.audioUrl);
    }
  };

  const handleLike = () => {
    if (blahblah.isLiked) {
      unlikeBlahblah(blahblah.id);
    } else {
      likeBlahblah(blahblah.id);
    }
  };

  const handleFavorite = () => {
    if (blahblah.isFavorited) {
      unfavoriteBlahblah(blahblah.id);
    } else {
      favoriteBlahblah(blahblah.id);
    }
  };

  return (
    <article className="bg-card border-b border-border p-4 transition-colors">
      {/* AI Title (for root posts with titles) */}
      {blahblah.aiTitle && !blahblah.parentId && (
        <Link to={`/blahblah/${blahblah.id}`} className="block mb-2">
          <h3 className="font-semibold text-card-foreground hover:text-primary transition-colors line-clamp-2">
            {blahblah.aiTitle}
          </h3>
        </Link>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/profile/${blahblah.user.id}`}>
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold shadow-md"
            style={{ backgroundColor: blahblah.user.profileColor || '#7c3aed' }}
          >
            {blahblah.user.displayName.charAt(0).toUpperCase()}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/profile/${blahblah.user.id}`}
              className="font-medium text-card-foreground hover:text-primary transition-colors"
            >
              {blahblah.user.displayName}
            </Link>
            {/* Category chips (first 2) */}
            {blahblah.categories && blahblah.categories.length > 0 && (
              <>
                {blahblah.categories.slice(0, 2).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/categories/${cat.slug}`}
                    className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full hover:bg-primary/20 transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {formatRelativeTime(blahblah.createdAt)}
          </p>
        </div>

        {/* Three-dot menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
          >
            <MoreIcon className="w-4 h-4 text-muted-foreground" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-44 bg-card border border-border rounded-lg shadow-lg py-1">
                <button
                  onClick={() => { setShowMenu(false); setShowShareModal(true); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <ShareGroupIcon className="w-4 h-4" />
                  Share to Group
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Share to Group Modal */}
      {showShareModal && (
        <ShareToGroupModal
          blahblahId={blahblah.id}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Audio player */}
      <div className="mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePlayPause}
            className={cn(
              'w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-md',
              isCurrentlyPlaying
                ? 'bg-primary text-primary-foreground scale-105'
                : 'bg-secondary text-primary hover:bg-secondary/80 hover:scale-105'
            )}
          >
            {isCurrentlyPlaying ? (
              <PauseIcon className="w-6 h-6" />
            ) : (
              <PlayIcon className="w-6 h-6 ml-0.5" />
            )}
          </button>

          <div className="flex-1">
            {/* Progress bar */}
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-100"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
              <span>{formatDuration(playbackProgress)}</span>
              <span>{formatDuration(blahblah.durationMs)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      {blahblah.tags && blahblah.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {blahblah.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded-full font-medium"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-4 text-muted-foreground pt-2">
          <button
            onClick={handleLike}
            className={cn(
              'flex items-center gap-1.5 transition-colors rounded-full px-3 py-1.5 -ml-3',
              blahblah.isLiked
                ? 'text-red-500 bg-red-500/10'
                : 'hover:text-red-500 hover:bg-red-500/10'
            )}
          >
            <HeartIcon filled={blahblah.isLiked} className="w-5 h-5" />
            <span className="text-sm font-medium">{blahblah.likesCount}</span>
          </button>

          <button
            onClick={handleFavorite}
            className={cn(
              'flex items-center gap-1.5 transition-colors rounded-full px-3 py-1.5',
              blahblah.isFavorited
                ? 'text-amber-500 bg-amber-500/10'
                : 'hover:text-amber-500 hover:bg-amber-500/10'
            )}
            title={blahblah.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <StarIcon filled={blahblah.isFavorited} className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate('/record', { state: { parentId: blahblah.id, parentBlahblah: blahblah } })}
            className="flex items-center gap-1.5 hover:text-primary hover:bg-primary/10 rounded-full px-3 py-1.5 transition-colors"
            title="Record a voice reply"
          >
            <MicIcon className="w-5 h-5" />
          </button>

          <Link
            to={`/blahblah/${blahblah.id}`}
            className="flex items-center gap-1.5 hover:text-primary hover:bg-primary/10 rounded-full px-3 py-1.5 transition-colors"
            title="View replies"
          >
            <CommentIcon className="w-5 h-5" />
            <span className="text-sm font-medium">{blahblah.replyCount}</span>
          </Link>

          <div className="flex items-center gap-1.5 px-3 py-1.5">
            <ViewIcon className="w-5 h-5" />
            <span className="text-sm font-medium">{blahblah.viewsCount}</span>
          </div>
        </div>
      )}
    </article>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
  );
}

function HeartIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}

function StarIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" strokeLinecap="round" />
      <line x1="8" y1="23" x2="16" y2="23" strokeLinecap="round" />
    </svg>
  );
}

function CommentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function ViewIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function ShareGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
